import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { 
  DollarSign, 
  Crown, 
  Calendar, 
  Plus, 
  List, 
  Edit, 
  Trash2, 
  Filter, 
  Search, 
  X, 
  Camera, 
  Image as ImageIcon, 
  ZoomIn,
  TrendingUp,
  TrendingDown,
  Target,
  BarChart3,
  FileText,
  Upload,
  AlertTriangle
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useSubscription } from "../contexts/SubscriptionContext";
import { useCurrency } from "../contexts/CurrencyContext";
import CurrencySelector from "./CurrencySelector";
import StatementImportModal from "./StatementImportModal";
import { showFeedbackToast } from "./ActionFeedback";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine
} from 'recharts';

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

// Format currency helper
const formatCurrency = (amount, currencyCode = 'USD') => {
  const currencyConfig = {
    USD: { symbol: '$', locale: 'en-US' },
    MXN: { symbol: 'MX$', locale: 'es-MX' },
    EUR: { symbol: '€', locale: 'de-DE' },
    GBP: { symbol: '£', locale: 'en-GB' },
    JPY: { symbol: '¥', locale: 'ja-JP' },
    CAD: { symbol: 'C$', locale: 'en-CA' },
    AUD: { symbol: 'A$', locale: 'en-AU' },
    CNY: { symbol: '¥', locale: 'zh-CN' },
    INR: { symbol: '₹', locale: 'en-IN' },
    BRL: { symbol: 'R$', locale: 'pt-BR' },
    SEK: { symbol: 'kr', locale: 'sv-SE' },
    RUB: { symbol: '₽', locale: 'ru-RU' }
  };
  
  const config = currencyConfig[currencyCode] || currencyConfig.USD;
  
  try {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  } catch {
    return `${config.symbol}${Math.round(amount || 0).toLocaleString()}`;
  }
};

// Expense Form Modal Component
const ExpenseFormModal = ({ expense, expenseOptions, selectedMonth, selectedYear, userProfile, onSave, onCancel, isPremium = false }) => {
  const { t } = useTranslation();
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const fileInputRef = useRef(null);
  
  // Currency options for display
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' }
  ];
  
  // Create default date for the selected month (current day)
  const getDefaultDate = () => {
    if (expense?.date) {
      return expense.date.split('T')[0];
    }
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    
    if (currentMonth === selectedMonth && currentYear === selectedYear) {
      return today.toISOString().split('T')[0];
    }
    
    const defaultDate = new Date(selectedYear, selectedMonth - 1, 1);
    return defaultDate.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    description: expense?.description || '',
    amount: expense?.amount || '',
    currency: expense?.currency || userProfile?.currencies?.[0]?.code || 'USD',
    category: expense?.category || '',
    payment_method: expense?.payment_method || '',
    date: getDefaultDate(),
    expense_type: expense?.expense_type || '',
    receipt_image: expense?.receipt_image || null
  });

  // Load existing receipt image preview
  useEffect(() => {
    if (expense?.receipt_image) {
      setReceiptPreview(expense.receipt_image);
    }
  }, [expense]);

  // Handle image upload
  const handleImageUpload = (e) => {
    if (!isPremium) {
      setShowUpgradePrompt(true);
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, WebP, HEIC)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setReceiptPreview(base64);
      setFormData(prev => ({ ...prev, receipt_image: base64 }));
    };
    reader.readAsDataURL(file);
  };

  // Handle camera capture (mobile)
  const handleCameraCapture = () => {
    if (!isPremium) {
      setShowUpgradePrompt(true);
      return;
    }
    fileInputRef.current?.click();
  };

  // Remove receipt image
  const handleRemoveImage = () => {
    setReceiptPreview(null);
    setFormData(prev => ({ ...prev, receipt_image: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.category) {
      toast.error(t('expenseTracker.fillRequired'));
      return;
    }

    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      payment_method: formData.payment_method === "none" || !formData.payment_method ? null : formData.payment_method,
      date: new Date(formData.date + 'T00:00:00.000Z').toISOString(),
      description: formData.description || formData.category,
      expense_type: formData.expense_type || null,
      receipt_image: formData.receipt_image || null
    };

    onSave(submitData);
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
            {expense ? t('expenseTracker.editExpense') : t('expenseTracker.addNewExpense')}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Row 1: Amount & Currency */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="amount" className="text-sm">{t('expenseTracker.amount')} *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  required
                  className="h-9"
                />
              </div>
              <div>
                <Label htmlFor="currency" className="text-sm">{t('expenseTracker.currency')} *</Label>
                <Select value={formData.currency} onValueChange={(value) => handleChange('currency', value)} modal={true}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {userProfile?.currencies?.map((currency, index) => (
                      <SelectItem key={`user-${currency.code}`} value={currency.code}>
                        {currencies.find(c => c.code === currency.code)?.symbol || currency.code} {currency.code} {currency.is_primary ? '- Primary' : ''}
                      </SelectItem>
                    ))}
                    {userProfile?.currencies?.length > 0 && (
                      <SelectItem value="divider" disabled>───────────</SelectItem>
                    )}
                    {currencies
                      .filter(c => !userProfile?.currencies?.some(uc => uc.code === c.code))
                      .map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Category & Date */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="category" className="text-sm">{t('expenseTracker.category')} *</Label>
                <Select value={formData.category} onValueChange={(value) => handleChange('category', value)} modal={true}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('expenseTracker.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseOptions.categories?.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {t(`expenseTracker.categories.${cat.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date" className="text-sm">{t('expenseTracker.date')} *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  required
                  className="h-9"
                />
              </div>
            </div>

            {/* Description - Full width */}
            <div>
              <Label htmlFor="description" className="text-sm">{t('expenseTracker.description')}</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('expenseTracker.descriptionPlaceholder')}
                className="h-9"
              />
            </div>

            {/* Row 3: Expense Type & Payment Method */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="expense_type" className="text-sm">{t('expenseTracker.expenseType')}</Label>
                <Select value={formData.expense_type || "not_specified"} onValueChange={(value) => handleChange('expense_type', value === "not_specified" ? "" : value)} modal={true}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('expenseTracker.selectPriority')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_specified">{t('expenseTracker.notSpecified')}</SelectItem>
                    <SelectItem value="basic">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        {t('expenseTracker.expenseTypes.basic')}
                      </span>
                    </SelectItem>
                    <SelectItem value="regular">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        {t('expenseTracker.expenseTypes.regular')}
                      </span>
                    </SelectItem>
                    <SelectItem value="luxury">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        {t('expenseTracker.expenseTypes.luxury')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment_method" className="text-sm">{t('expenseTracker.paymentMethod')}</Label>
                <Select value={formData.payment_method || "none"} onValueChange={(value) => handleChange('payment_method', value)} modal={true}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('expenseTracker.selectPaymentMethod')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('expenseTracker.noPaymentMethod')}</SelectItem>
                    {expenseOptions.payment_methods?.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {t(`expenseTracker.paymentMethods.${method.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Receipt Image Upload - Compact */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm">{t('expenseTracker.receiptOptional')}</Label>
                {!isPremium && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    Premium
                  </span>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              <input
                id="gallery-input"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              {receiptPreview ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img 
                    src={receiptPreview} 
                    alt="Receipt preview" 
                    className="w-full h-20 object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCameraCapture}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 border-2 border-dashed rounded-lg transition-colors text-sm ${
                      isPremium 
                        ? 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50 text-gray-600 hover:text-emerald-600'
                        : 'border-gray-200 text-gray-400 cursor-pointer'
                    }`}
                  >
                    <Camera className="w-4 h-4" />
                    <span>Camera</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isPremium) {
                        setShowUpgradePrompt(true);
                        return;
                      }
                      document.getElementById('gallery-input')?.click();
                    }}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 border-2 border-dashed rounded-lg transition-colors text-sm ${
                      isPremium 
                        ? 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50 text-gray-600 hover:text-emerald-600'
                        : 'border-gray-200 text-gray-400 cursor-pointer'
                    }`}
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span>Gallery</span>
                  </button>
                </div>
              )}
              
              {showUpgradePrompt && (
                <div className="p-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Crown className="w-4 h-4 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-amber-800">Premium Feature</p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Attach receipts to your expenses with Premium.
                      </p>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setShowUpgradePrompt(false);
                            window.location.hash = '#upgrade';
                          }}
                          className="px-2 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 transition-colors"
                        >
                          Upgrade
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowUpgradePrompt(false)}
                          className="px-2 py-1 text-amber-700 text-xs hover:bg-amber-100 rounded transition-colors"
                        >
                          Later
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons - Sticky on mobile */}
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-1">
              <Button type="button" variant="outline" onClick={onCancel} className="h-9 px-4">
                {t('expenseTracker.cancel')}
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 h-9 px-4">
                {expense ? t('expenseTracker.updateExpense') : t('expenseTracker.saveExpense')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main Expense Tracker Module Component
const ExpenseTrackerModule = ({ initialShowAddForm = false }) => {
  const { t, i18n } = useTranslation();
  const { isPremium } = useSubscription();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalExpenses, setTotalExpenses] = useState(0);
  
  // Selected expense for detail view with receipt
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showReceiptZoom, setShowReceiptZoom] = useState(false);
  
  // User profile state for multi-currency support
  const [userProfile, setUserProfile] = useState(null);
  
  // Month/Year selection state - default to current month
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  
  const [filters, setFilters] = useState({
    category: '',
    payment_method: '',
    expense_type: '',
    min_amount: '',
    max_amount: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  // Default expense options as fallback
  const defaultExpenseOptions = {
    categories: [
      { value: 'food', label: 'food' },
      { value: 'rent', label: 'rent' },
      { value: 'transport', label: 'transport' },
      { value: 'entertainment', label: 'entertainment' },
      { value: 'healthcare', label: 'healthcare' },
      { value: 'utilities', label: 'utilities' },
      { value: 'groceries', label: 'groceries' },
      { value: 'dining', label: 'dining' },
      { value: 'gas', label: 'gas' },
      { value: 'shopping', label: 'shopping' },
      { value: 'phone', label: 'phone' },
      { value: 'internet', label: 'internet' },
      { value: 'insurance', label: 'insurance' },
      { value: 'education', label: 'education' },
      { value: 'fitness', label: 'fitness' },
      { value: 'travel', label: 'travel' },
      { value: 'household', label: 'household' },
      { value: 'other', label: 'other' }
    ],
    payment_methods: [
      { value: 'credit_card', label: 'credit_card' },
      { value: 'debit_card', label: 'debit_card' },
      { value: 'cash', label: 'cash' },
      { value: 'bank_transfer', label: 'bank_transfer' },
      { value: 'apple_pay', label: 'apple_pay' },
      { value: 'paypal', label: 'paypal' }
    ],
    expense_types: [
      { value: 'basic', label: 'basic' },
      { value: 'regular', label: 'regular' },
      { value: 'luxury', label: 'luxury' }
    ],
    budget_items: []
  };

  const [expenseOptions, setExpenseOptions] = useState(defaultExpenseOptions);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showAddForm, setShowAddForm] = useState(initialShowAddForm);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, expenseId: null, expenseName: '' });

  // Open form when initialShowAddForm prop changes to true
  useEffect(() => {
    if (initialShowAddForm) {
      setShowAddForm(true);
    }
  }, [initialShowAddForm]);
  
  // Statement Import Modal state
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Duplicate detection state
  const [duplicateWarning, setDuplicateWarning] = useState({ show: false, candidates: [], newExpense: null });
  
  // Uncategorized reminders state
  const [reminders, setReminders] = useState({ items: [], show: false, totalCount: 0 });
  
  // Comparison data state
  const [monthlyTarget, setMonthlyTarget] = useState(0);
  const [previousMonthData, setPreviousMonthData] = useState({ amount: 0, month: '', year: 0 });
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [monthTargets, setMonthTargets] = useState({});

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken') || sessionStorage.getItem('session_token');
    console.log('[Expenses] getAuthHeaders - token found:', !!token);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch monthly target for selected month
  const fetchMonthlyTarget = async () => {
    try {
      const response = await axios.get(`${API}/monthly-targets/${selectedMonth}/${selectedYear}`, {
        headers: getAuthHeaders()
      });
      setMonthlyTarget(response.data.target_amount || 0);
    } catch (error) {
      console.error('Error fetching monthly target:', error);
      setMonthlyTarget(0);
    }
  };

  // Fetch expense options on component mount
  useEffect(() => {
    fetchExpenseOptions();
    fetchUserProfile();
    fetchReminders();
  }, []);

  // Fetch monthly target when month/year changes
  useEffect(() => {
    fetchMonthlyTarget();
  }, [selectedMonth, selectedYear]);

  // Fetch expenses when filters, sorting, or selected month/year changes
  useEffect(() => {
    fetchExpenses();
    fetchPreviousMonthData();
  }, [filters, sortBy, sortOrder, selectedMonth, selectedYear]);

  const fetchExpenseOptions = async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      console.log('[ExpenseOptions] No auth token, using defaults');
      return;
    }
    
    try {
      console.log('[ExpenseOptions] Fetching options...');
      const response = await axios.get(`${API}/expenses/options`, {
        headers,
        timeout: 10000
      });
      console.log('[ExpenseOptions] Options received:', response.data?.categories?.length, 'categories');
      setExpenseOptions(response.data);
    } catch (error) {
      console.error('[ExpenseOptions] Error fetching:', error.message);
      // Keep using default options
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, {
        headers: getAuthHeaders()
      });
      const profile = response.data;
      if (!profile.currencies || profile.currencies.length === 0) {
        profile.currencies = [{ code: profile.currency || 'USD', is_primary: true, order: 0 }];
      }
      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile({
        currencies: [{ code: 'USD', is_primary: true, order: 0 }],
        currency: 'USD'
      });
    }
  };

  const fetchPreviousMonthData = async () => {
    let prevMonth = selectedMonth - 1;
    let prevYear = selectedYear;
    
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear = selectedYear - 1;
    }
    
    const monthName = new Date(prevYear, prevMonth - 1).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', { month: 'long' });
    
    try {
      const startDate = new Date(prevYear, prevMonth - 1, 1);
      const endDate = new Date(prevYear, prevMonth, 0);
      
      const params = new URLSearchParams();
      params.append('start_date', startDate.toISOString().split('T')[0]);
      params.append('end_date', endDate.toISOString().split('T')[0]);
      
      const response = await axios.get(`${API}/expenses?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      const prevTotal = response.data.expenses ? 
        response.data.expenses.reduce((sum, expense) => sum + expense.amount, 0) : 0;
      
      setPreviousMonthData({
        amount: prevTotal,
        month: monthName,
        year: prevYear
      });
    } catch (error) {
      console.error('Error fetching previous month data:', error);
      setPreviousMonthData({ 
        amount: 0, 
        month: monthName, 
        year: prevYear 
      });
    }
  };

  const fetchExpenses = async () => {
    // Check if we have a valid auth token
    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      console.log('[Expenses] No auth token available, skipping fetch');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      const monthStartDate = new Date(selectedYear, selectedMonth - 1, 1);
      const monthEndDate = new Date(selectedYear, selectedMonth, 0);
      
      const effectiveStartDate = filters.start_date || monthStartDate.toISOString().split('T')[0];
      const effectiveEndDate = filters.end_date || monthEndDate.toISOString().split('T')[0];
      
      params.append('start_date', effectiveStartDate);
      params.append('end_date', effectiveEndDate);
      
      if (filters.category) params.append('category', filters.category);
      if (filters.payment_method) params.append('payment_method', filters.payment_method);
      if (filters.min_amount) params.append('min_amount', filters.min_amount);
      if (filters.max_amount) params.append('max_amount', filters.max_amount);
      
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('limit', '50');

      console.log('[Expenses] Fetching with params:', params.toString());
      
      const response = await axios.get(`${API}/expenses?${params.toString()}`, {
        headers,
        timeout: 30000
      });

      console.log('[Expenses] Response received:', response.data?.total, 'expenses');
      setExpenses(response.data.expenses || []);
      setTotalExpenses(response.data.total || 0);
    } catch (error) {
      console.error('[Expenses] Error fetching expenses:', error.message);
      console.error('[Expenses] Error details:', error.response?.data || error);
      
      const isSpanish = i18n.language === 'es';
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error(isSpanish ? 'Tiempo de espera agotado. Intenta de nuevo.' : 'Request timed out. Please try again.');
      } else if (error.response?.status === 401) {
        toast.error(isSpanish ? 'Sesión expirada. Por favor inicia sesión de nuevo.' : 'Session expired. Please log in again.');
      } else {
        toast.error(isSpanish ? 'Error al cargar gastos' : 'Failed to load expenses');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = (expense) => {
    setDeleteConfirmation({
      show: true,
      expenseId: expense.id,
      expenseName: expense.description
    });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/expenses/${deleteConfirmation.expenseId}`, {
        headers: getAuthHeaders()
      });
      toast.success(t('expenseTracker.deleteSuccess'));
      fetchExpenses();
      setDeleteConfirmation({ show: false, expenseId: null, expenseName: '' });
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error(t('expenseTracker.deleteError'));
      setDeleteConfirmation({ show: false, expenseId: null, expenseName: '' });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, expenseId: null, expenseName: '' });
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowAddForm(true);
  };

  const handleAddExpense = async (expenseData) => {
    try {
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, expenseData, {
          headers: getAuthHeaders()
        });
        toast.success(t('expenseTracker.updateSuccess'));
      } else {
        const response = await axios.post(`${API}/expenses`, expenseData, {
          headers: getAuthHeaders()
        });
        
        // Check for duplicate candidates
        if (response.data.duplicate_candidates && response.data.duplicate_candidates.length > 0) {
          setDuplicateWarning({
            show: true,
            candidates: response.data.duplicate_candidates,
            newExpense: response.data.expense
          });
          toast.warning(
            i18n.language?.startsWith('es')
              ? `Se encontraron ${response.data.duplicate_candidates.length} posible(s) duplicado(s)`
              : `Found ${response.data.duplicate_candidates.length} potential duplicate(s)`,
            { duration: 5000 }
          );
        } else {
          // Show gamification feedback toast instead of plain success
          const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
          showFeedbackToast({
            confirmation: lang === 'es' ? 'Gasto registrado' : 'Expense logged',
            reinforcement: lang === 'es' ? 'Estás tomando control de tu dinero' : "You're taking control of your money",
            progressUpdate: '',
            progressDelta: 0
          }, 'expenseLogged');
          
          // Dispatch event to refresh dashboard data from backend
          window.dispatchEvent(new CustomEvent('expenseTracked'));
        }
      }
      
      setShowAddForm(false);
      setEditingExpense(null);
      fetchExpenses();
      // Refresh reminders after adding expense
      fetchReminders();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(t('expenseTracker.saveError'));
    }
  };
  
  // Fetch uncategorized reminders
  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/expenses/uncategorized-reminders`, {
        headers: getAuthHeaders()
      });
      if (response.data.total_count > 0) {
        setReminders({
          items: response.data.reminders,
          show: true,
          totalCount: response.data.total_count,
          uncategorizedCount: response.data.uncategorized_count,
          unclassifiedCount: response.data.unclassified_count
        });
      } else {
        setReminders({ items: [], show: false, totalCount: 0 });
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };
  
  // Handle dismissing a duplicate (mark as not duplicate)
  const handleDismissDuplicate = () => {
    setDuplicateWarning({ show: false, candidates: [], newExpense: null });
    toast.success(
      i18n.language?.startsWith('es')
        ? 'Gasto guardado - marcado como no duplicado'
        : 'Expense saved - marked as not duplicate'
    );
  };
  
  // Handle confirming duplicate (delete the new expense)
  const handleConfirmDuplicate = async () => {
    if (duplicateWarning.newExpense?.id) {
      try {
        await axios.delete(`${API}/expenses/${duplicateWarning.newExpense.id}`, {
          headers: getAuthHeaders()
        });
        toast.info(
          i18n.language?.startsWith('es')
            ? 'Gasto eliminado (era duplicado)'
            : 'Expense removed (was duplicate)'
        );
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting duplicate:', error);
      }
    }
    setDuplicateWarning({ show: false, candidates: [], newExpense: null });
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      payment_method: '',
      expense_type: '',
      min_amount: '',
      max_amount: '',
      start_date: '',
      end_date: '',
      search: ''
    });
  };

  const handleFilterChange = (key, value) => {
    const filterValue = value === "all" ? "" : value;
    setFilters(prev => ({ ...prev, [key]: filterValue }));
  };

  // Filter expenses by search term
  const filteredExpenses = expenses.filter(expense => {
    if (!filters.search) return true;
    const searchTerm = filters.search.toLowerCase();
    return (
      expense.description.toLowerCase().includes(searchTerm) ||
      expense.amount.toString().includes(searchTerm) ||
      expense.category.toLowerCase().includes(searchTerm)
    );
  });

  const getCurrentMonthTotal = () => {
    return filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  // Get totals grouped by currency
  const getTotalsByCurrency = () => {
    const totals = {};
    filteredExpenses.forEach(expense => {
      const currency = expense.currency || 'USD';
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      totals[currency] += expense.amount;
    });
    return totals;
  };

  // Get unique currencies from user's expenses
  const getExpenseCurrencies = () => {
    const currencies = new Set();
    filteredExpenses.forEach(expense => {
      currencies.add(expense.currency || 'USD');
    });
    return Array.from(currencies);
  };

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
      <CardHeader>
        {/* Title row with Add Expense button */}
        <div className="flex flex-col gap-3">
          <CardTitle className="flex items-center gap-2 text-black text-2xl font-bold">
            <List className="w-5 h-5" />
            {t('expenseTracker.pageTitle')}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-red-600 hover:bg-red-700 font-bold text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('expenseTracker.addExpense')}
            </Button>
            <Button
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50 text-sm"
            >
              <FileText className="w-4 h-4 mr-1" />
              {i18n.language?.startsWith('es') ? 'Importar' : 'Import'}
            </Button>
          </div>
        </div>
        
        <div className="flex items-center justify-between gap-4 flex-wrap mt-2">
          <CardDescription className="flex-1">
            {t('expenseTracker.pageDescription')}
          </CardDescription>
          <CurrencySelector />
        </div>
        
        {/* Summary */}
        <Card className="bg-emerald-50 border-emerald-200 mt-4">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start mb-4">
              <span className="text-lg font-medium">{t('expenseTracker.totalExpenses')}</span>
              <div className="text-right">
                {(() => {
                  const totals = getTotalsByCurrency();
                  const currencies = Object.keys(totals);
                  if (currencies.length <= 1) {
                    // Single currency - show simple total
                    return (
                      <span className="text-2xl font-bold text-emerald-700">
                        {formatCurrency(getCurrentMonthTotal(), currencies[0] || 'USD')}
                      </span>
                    );
                  } else {
                    // Multiple currencies - show each total with currency label
                    return (
                      <div className="flex flex-col gap-1">
                        {currencies.map(currency => (
                          <div key={currency} className="flex items-center justify-end gap-2">
                            <span className="text-sm text-emerald-600 font-medium">{currency}:</span>
                            <span className="text-xl font-bold text-emerald-700">
                              {formatCurrency(totals[currency], currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
            
            <div className="text-sm text-emerald-600">
              {filteredExpenses.length} {t('expenseTracker.expensesShowing')} / {totalExpenses} {t('expenseTracker.total')}
            </div>
          </CardContent>
        </Card>
      </CardHeader>

      <CardContent>
        <div className="space-y-6">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              {/* Month/Year Selector */}
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-emerald-200">
                <Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {t(`expenseTracker.months.${['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'][i]}`)}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = currentDate.getFullYear() - 5 + i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                {t('expenseTracker.filters')}
              </Button>
            </div>
            
            {/* Search and Sort */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder={t('expenseTracker.searchPlaceholder')}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-48"
                />
              </div>
              <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
                const [field, order] = value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-desc">{t('expenseTracker.sortDateNewest')}</SelectItem>
                  <SelectItem value="date-asc">{t('expenseTracker.sortDateOldest')}</SelectItem>
                  <SelectItem value="amount-desc">{t('expenseTracker.sortAmountHigh')}</SelectItem>
                  <SelectItem value="amount-asc">{t('expenseTracker.sortAmountLow')}</SelectItem>
                  <SelectItem value="description-asc">{t('expenseTracker.sortDescription')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <Card className="bg-white p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <Label>{t('expenseTracker.category')}</Label>
                  <Select value={filters.category || "all"} onValueChange={(value) => handleFilterChange('category', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('expenseTracker.allCategories')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('expenseTracker.allCategories')}</SelectItem>
                      {expenseOptions.categories?.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {t(`expenseTracker.categories.${cat.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('expenseTracker.paymentMethod')}</Label>
                  <Select value={filters.payment_method || "all"} onValueChange={(value) => handleFilterChange('payment_method', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('expenseTracker.allPaymentMethods')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('expenseTracker.allPaymentMethods')}</SelectItem>
                      {expenseOptions.payment_methods?.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {t(`expenseTracker.paymentMethods.${method.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{t('expenseTracker.minAmount')}</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.min_amount}
                    onChange={(e) => handleFilterChange('min_amount', e.target.value)}
                  />
                </div>

                <div>
                  <Label>{t('expenseTracker.maxAmount')}</Label>
                  <Input
                    type="number"
                    placeholder="1000"
                    value={filters.max_amount}
                    onChange={(e) => handleFilterChange('max_amount', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={clearFilters} size="sm">
                  {t('expenseTracker.clearFilters')}
                </Button>
              </div>
            </Card>
          )}

          {/* Expense List */}
          <Card className="bg-white">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                {t('expenseTracker.loading')}
              </div>
            ) : filteredExpenses.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {t('expenseTracker.noExpenses')}
              </div>
            ) : (
              <div className="divide-y">
                {filteredExpenses.map((expense) => (
                  <div 
                    key={expense.id} 
                    className={`p-3 sm:p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedExpense?.id === expense.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                    }`}
                    onClick={() => setSelectedExpense(selectedExpense?.id === expense.id ? null : expense)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 relative">
                            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                            {expense.receipt_image && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                <ImageIcon className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 text-sm sm:text-base truncate">
                              {expense.description}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 space-x-1 sm:space-x-2 mt-1">
                              <span>{t(`expenseTracker.categories.${expense.category}`)}</span>
                              {expense.payment_method && (
                                <>
                                  <span>•</span>
                                  <span className="hidden sm:inline">{t(`expenseTracker.paymentMethods.${expense.payment_method}`)}</span>
                                </>
                              )}
                              <span>•</span>
                              <span>{new Date(expense.date).toLocaleDateString()}</span>
                              {expense.expense_type && (
                                <span className={`inline-block px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs ml-1 ${
                                  expense.expense_type === 'basic' ? 'bg-green-100 text-green-700' :
                                  expense.expense_type === 'regular' ? 'bg-blue-100 text-blue-700' :
                                  'bg-purple-100 text-purple-700'
                                }`}>
                                  {t(`expenseTracker.expenseTypes.${expense.expense_type}`)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
                        <div className="text-right">
                          <div className="font-semibold text-gray-900 text-sm sm:text-base">
                            <span className="text-gray-500 text-xs mr-1">{expense.currency || 'USD'}</span>
                            {formatCurrency(expense.amount, expense.currency || 'USD')}
                          </div>
                        </div>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditExpense(expense)}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                          >
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteExpense(expense)}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Receipt Preview */}
                    {selectedExpense?.id === expense.id && expense.receipt_image && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-start gap-3">
                          <div 
                            className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 cursor-pointer group"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowReceiptZoom(true);
                            }}
                          >
                            <img 
                              src={expense.receipt_image} 
                              alt="Receipt" 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ZoomIn className="w-6 h-6 text-white" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-600">Receipt attached</p>
                            <p className="text-xs text-gray-400 mt-1">Click image to zoom</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmation.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('expenseTracker.confirmDelete')}</h3>
              <p className="text-gray-600 mb-6">
                {t('expenseTracker.deleteConfirmMessage', { name: deleteConfirmation.expenseName })}
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={cancelDelete}>
                  {t('expenseTracker.cancel')}
                </Button>
                <Button 
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t('expenseTracker.delete')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Expense Modal */}
        {showAddForm && (
          <ExpenseFormModal
            expense={editingExpense}
            expenseOptions={expenseOptions}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            userProfile={userProfile}
            onSave={handleAddExpense}
            onCancel={() => {
              setShowAddForm(false);
              setEditingExpense(null);
            }}
            isPremium={isPremium}
          />
        )}

        {/* Receipt Zoom Modal */}
        {showReceiptZoom && selectedExpense?.receipt_image && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReceiptZoom(false)}
          >
            <button
              onClick={() => setShowReceiptZoom(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="max-w-4xl max-h-[90vh] overflow-auto">
              <img 
                src={selectedExpense.receipt_image} 
                alt="Receipt" 
                className="max-w-full h-auto rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg text-sm">
              {selectedExpense.description} • {formatCurrency(selectedExpense.amount, selectedExpense.currency || 'USD')}
            </div>
          </div>
        )}

        {/* Statement Import Modal */}
        <StatementImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={(count) => {
            setShowImportModal(false);
            fetchExpenses();
            fetchReminders();
            toast.success(
              i18n.language?.startsWith('es') 
                ? `Se importaron ${count} gastos correctamente`
                : `${count} expenses imported successfully`
            );
          }}
          token={sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken') || sessionStorage.getItem('session_token')}
        />
        
        {/* Duplicate Warning Modal */}
        {duplicateWarning.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {i18n.language?.startsWith('es') ? 'Posible Duplicado' : 'Possible Duplicate'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {i18n.language?.startsWith('es') 
                      ? 'Encontramos gastos similares existentes'
                      : 'We found similar existing expenses'}
                  </p>
                </div>
              </div>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-emerald-800 mb-1">
                  {i18n.language?.startsWith('es') ? 'Gasto que agregaste:' : 'Expense you added:'}
                </p>
                <p className="text-emerald-700">
                  {duplicateWarning.newExpense?.description} - {formatCurrency(duplicateWarning.newExpense?.amount || 0, duplicateWarning.newExpense?.currency || 'USD')}
                </p>
                <p className="text-xs text-emerald-600">{duplicateWarning.newExpense?.date}</p>
              </div>
              
              <div className="space-y-2 mb-6 max-h-48 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700">
                  {i18n.language?.startsWith('es') ? 'Gastos similares encontrados:' : 'Similar expenses found:'}
                </p>
                {duplicateWarning.candidates.map((candidate, idx) => (
                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{candidate.description}</p>
                        <p className="text-sm text-gray-600">{formatCurrency(candidate.amount, 'USD')} • {candidate.date}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        candidate.type === 'imported_statement' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {candidate.type === 'imported_statement' 
                          ? (i18n.language?.startsWith('es') ? 'Importado' : 'Imported')
                          : (i18n.language?.startsWith('es') ? 'Manual' : 'Manual')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleDismissDuplicate}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {i18n.language?.startsWith('es') ? 'No es duplicado, mantener' : 'Not duplicate, keep it'}
                </Button>
                <Button
                  onClick={handleConfirmDuplicate}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                >
                  {i18n.language?.startsWith('es') ? 'Es duplicado, eliminar' : 'Is duplicate, remove'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Uncategorized Reminders Banner */}
        {reminders.show && reminders.totalCount > 0 && (
          <div className="fixed bottom-4 right-4 z-40 max-w-sm">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl shadow-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    {i18n.language?.startsWith('es') 
                      ? 'Gastos necesitan atención'
                      : 'Expenses need attention'}
                  </h4>
                  <p className="text-sm text-white/90 mb-3">
                    {i18n.language?.startsWith('es')
                      ? `${reminders.totalCount} gasto(s) importado(s) necesitan categorización o clasificación`
                      : `${reminders.totalCount} imported expense(s) need categorization or classification`}
                  </p>
                  <div className="flex gap-2 text-xs">
                    {reminders.uncategorizedCount > 0 && (
                      <span className="bg-white/20 px-2 py-1 rounded-full">
                        {reminders.uncategorizedCount} {i18n.language?.startsWith('es') ? 'sin categoría' : 'uncategorized'}
                      </span>
                    )}
                    {reminders.unclassifiedCount > 0 && (
                      <span className="bg-white/20 px-2 py-1 rounded-full">
                        {reminders.unclassifiedCount} {i18n.language?.startsWith('es') ? 'sin tipo' : 'unclassified'}
                      </span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => setReminders(prev => ({ ...prev, show: false }))}
                  className="text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpenseTrackerModule;
export { ExpenseFormModal };
